import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Step,
  StepButton,
  StepConnector,
  StepLabel,
  Stepper,
  Typography,
  colors,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {
  User as UserIcon,
  Star as StarIcon,
  Briefcase as BriefcaseIcon,
  File as FileIcon,
  Instagram as InstagramIcon,
} from 'react-feather';
import { PhoneIphone as PhoneIphoneIcon } from '@mui/icons-material';

import Page from '../Page';
import InstagramQuickStart from './InstagramQuickStart';
import UserDetails from './UserDetails';
import AppDetails from './AppDetails';
import NextStepsHelp from './NextStepsHelp';
import AppContent from './AppContent';
import { useQuery } from '@apollo/client/react';
import { GET_ONBOARDING } from '../../graphql/onboarding';
import ServerError from '../error/ServerError';
import Loading from '../shared/Loading';
import config from '../../config';

const steps = [
  {
    label: 'Quick Start',
    value: 'quickstart',
    icon: InstagramIcon,
  },
  {
    label: 'Profile',
    value: 'profile',
    icon: UserIcon,
  },
  {
    label: 'App',
    value: 'app',
    icon: BriefcaseIcon,
  },
  {
    label: 'Content',
    value: 'content',
    icon: FileIcon,
  },
  {
    label: 'Preview',
    value: 'preview',
    icon: PhoneIphoneIcon,
  },
];

const PREVIEW_INDEX = steps.findIndex((s) => s.value === 'preview');

const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  '&.MuiStepConnector-vertical': {
    marginLeft: 19,
    padding: 0,
  },
  '& .MuiStepConnector-line': {
    borderColor: theme.palette.divider,
  },
}));

const CustomStepIcon = ({ active, completed, icon }) => {
  const Icon = steps[icon - 1].icon;

  return (
    <Avatar
      sx={(theme) => ({
        ...(active && {
          backgroundColor: theme.palette.secondary.main,
          boxShadow: theme.shadows[10],
          color: theme.palette.secondary.contrastText,
        }),
        ...(completed && {
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.secondary.contrastText,
        }),
      })}
    >
      <Icon size="20" />
    </Avatar>
  );
};

CustomStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
  icon: PropTypes.number.isRequired,
};

const Onboarding = () => {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const params = useParams();
  const { error, loading, data } = useQuery(GET_ONBOARDING);
  useEffect(() => {
    if (params.tab) {
      const stepIndex = steps.findIndex((step) => step.value === params.tab);
      if (stepIndex >= 0) setActiveStep(stepIndex);
    }
  }, [params]);

  if (error) {
    return <ServerError message={error.message} />;
  }

  if (loading) {
    return <Loading />;
  }

  const goToStep = (index) => {
    setActiveStep(index);
    const step = steps[index];
    navigate(`/trainer/onboarding/${step.value}`);
  };

  const handleNext = () => goToStep(activeStep + 1);
  const handleBack = () => goToStep(activeStep - 1);
  const handleQuickStartComplete = () => goToStep(PREVIEW_INDEX);
  const handleStepClick = (index) => () => goToStep(index);

  const onPreview = activeStep === PREVIEW_INDEX;

  return (
    <Page title={`Build your fitness app with ${config.appDisplayName}`}>
      <Container maxWidth="lg">
        <Box mb={3}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              variant="body1"
              color="inherit"
              to="/trainer"
              component={RouterLink}
            >
              {config.appDisplayName}
            </Link>
          </Breadcrumbs>
          <Typography variant="h3" color="textPrimary">
            Get Started Building your Fitness App
          </Typography>
        </Box>
        <Paper>
          <Grid container>
            <Grid item xs={12} md={3}>
              <Stepper
                activeStep={activeStep}
                sx={{ backgroundColor: 'transparent' }}
                connector={<CustomStepConnector />}
                orientation="vertical"
                nonLinear
              >
                {steps.map((step, index) => (
                  <Step key={step.label}>
                    <StepButton
                      onClick={handleStepClick(index)}
                      icon={index + 1}
                    >
                      <StepLabel StepIconComponent={CustomStepIcon}>
                        {step.label}
                      </StepLabel>
                    </StepButton>
                  </Step>
                ))}
              </Stepper>
            </Grid>
            <Grid item xs={12} md={9}>
              <Box p={3}>
                {activeStep === 0 && (
                  <InstagramQuickStart
                    onComplete={handleQuickStartComplete}
                    onSkip={handleNext}
                  />
                )}
                {activeStep === 1 && (
                  <UserDetails
                    onBack={handleBack}
                    onNext={handleNext}
                    me={data.me}
                    appInfo={data.appInfo}
                  />
                )}
                {activeStep === 2 && (
                  <AppDetails
                    onBack={handleBack}
                    onNext={handleNext}
                    appInfo={data.appInfo}
                  />
                )}
                {activeStep === 3 && (
                  <AppContent
                    onBack={handleBack}
                    appInfo={data.appInfo}
                    onNext={handleNext}
                  />
                )}
                {onPreview && (
                  <Box>
                    <Box display="flex" justifyContent="center">
                      <Avatar sx={{ backgroundColor: colors.red[600] }}>
                        <StarIcon />
                      </Avatar>
                    </Box>
                    <Box mt={2}>
                      <Typography
                        variant="h3"
                        color="textPrimary"
                        align="center"
                        gutterBottom
                      >
                        Your app is ready to preview!
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        color="textSecondary"
                        align="center"
                      >
                        Use the steps on the left to fine-tune your profile,
                        app, or content.
                      </Typography>
                    </Box>

                    <NextStepsHelp />

                    <Box mt={2} display="flex" justifyContent="center">
                      <Button
                        variant="contained"
                        color="secondary"
                        component={RouterLink}
                        to="/trainer/training_plans"
                      >
                        Continue to Setup Your App
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Page>
  );
};

export default Onboarding;
